/*
 * Sprint V30 (Fr 27.02.2026) — Retention Upgrade Deal-Karte (Pricing-Seite).
 * Prominente Karte OBEN auf der Pricing-Seite fuer Pro-Solo-Monatsabonnenten
 * innerhalb der 28-Tage-Upgrade-Fenster.
 * Andreas-Direktive: prominent, aber nicht zu salesy. Deal klar kommuniziert.
 */
import React, { useEffect, useState } from "react";
import { Sparkles, Check, ArrowRight, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { trackAddToCart, trackCheckoutStart, trackUpgradeClick } from "@/lib/ga4";

export default function UpgradeOfferPricingCard() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/upgrade-offer/status")
      .then(({ data }) => { if (alive) setStatus(data); })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);

  if (!status?.eligible) return null;

  const days = status.days_remaining ?? 0;
  const hours = status.hours_remaining ?? 0;
  const mode = status.display_mode || "days";
  const checkoutUrl = status.checkout_url || "";

  const timerLabel = mode === "hours"
    ? `noch ${hours} ${hours === 1 ? "Stunde" : "Stunden"}`
    : `noch ${days} ${days === 1 ? "Tag" : "Tage"}`;

  const handleClick = () => {
    api.post("/upgrade-offer/clicked").catch(() => {});
    // Sprint V33 (Sa 14.09.2026) — Funnel-Events: add_to_cart + upgrade_click + begin_checkout
    trackUpgradeClick({ location: "retention_upgrade_offer", plan: "pro_solo_annual" });
    trackAddToCart({ plan: "pro_solo_annual", value: 349, location: "retention_upgrade_offer" });
    trackCheckoutStart({ plan: "pro_solo_annual", value: 349 });
    if (checkoutUrl) window.location.href = checkoutUrl;
  };

  return (
    <section
      data-testid="pricing-upgrade-offer-card"
      className="mb-8 rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-amber-50 dark:from-amber-950/40 dark:via-slate-900 dark:to-amber-950/30 dark:border-amber-800/70 p-6 sm:p-8 shadow-md"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold uppercase tracking-widest px-2.5 py-1">
            <Sparkles className="w-3 h-3" /> Dein Jahres-Vorteil
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-800/50 text-amber-800 dark:text-amber-200 text-xs font-semibold px-3 py-1">
          <Clock className="w-3.5 h-3.5" /> {timerLabel} gültig
        </span>
      </div>
      <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
        Wechsel jetzt aufs Jahresabo — und wir schenken dir das Fokus-Typen-PowerPaket
      </h2>
      <p className="mt-2 text-sm sm:text-base text-slate-700 dark:text-slate-200 max-w-2xl">
        Als Pro-Solo-Monatskunde hast du <b>{timerLabel}</b>, das Jahresabo mit dem
        wertvollsten Bonus zu buchen — und zwar bevor die erste Verlängerung deines
        Monatsabos greift.
      </p>

      <ul className="mt-5 space-y-2 text-sm text-slate-800 dark:text-slate-100">
        <li className="flex items-start gap-2">
          <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <span><b>Pro Solo Jahr</b> — der volle Zugang für 12 Monate zu 349 €</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <span><b>Fokus-Typen-PowerPaket Lifetime</b> — der Online-Kurs (Wert 297 €) dauerhaft, ohne Aufpreis</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <span><b>Dein Monatsbeitrag</b> (39 €) wird nach dem Wechsel voll erstattet — persönlich innerhalb weniger Tage</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <span><b>Aktueller Monat bleibt aktiv</b> — dein laufender Zugang läuft regulär bis Monatsende weiter, on top zum Jahresabo</span>
        </li>
      </ul>

      <div className="mt-6 flex items-center gap-4 flex-wrap">
        <button
          type="button"
          onClick={handleClick}
          disabled={!checkoutUrl}
          data-testid="pricing-upgrade-offer-cta"
          className="inline-flex items-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm sm:text-base px-6 py-3 shadow-md transition-colors"
        >
          Auf Jahresabo wechseln <ArrowRight className="w-4 h-4" />
        </button>
        <span className="text-xs text-slate-600 dark:text-slate-400">
          {checkoutUrl
            ? "Sichere Zahlung via Ablefy · Widerrufsverzicht bei Aktivierung"
            : "Checkout wird gerade eingerichtet"}
        </span>
      </div>
    </section>
  );
}
